// Balls of Fire audio engine.
// Original WAVs live BYTE-FOR-BYTE (checksum-verified) in
// /games/balls-of-fire/audio/source/ and are NEVER modified. All runtime
// gain / fades / ducking are applied via HTML5 Audio volume only (runtime
// derivatives), so originals stay untouched. Music + SFX buses are independent,
// with mute; music ducks ~5 dB under cues; recovery resumes music WITHOUT
// replaying the trigger stinger; one-shots never loop.
//
// MUSIC MAP (owner request 2026-06):
//   - BASE game play      -> bbMusic  (02_Burning_Balls_Bonus_Background.wav)
//   - Burning Balls bonus -> bbVox one-shot intro (owner recording) + slight pause,
//                            then stinger(03) → freeMusic (01) loops through the bonus
//   - Free Games          -> freeMusic (01) LOOPS underneath; freeVox (owner recording,
//                            pitched up) plays ONCE at the intro as the scatters hit
//   After any bonus/free feature ends we resume BASE music (bbMusic).
//
// SFX (reel start/stop, regular win, big win, coin) are SYNTHESIZED in-browser
// via Web Audio (royalty-free, no files) — see the "SFX bus" section below.
const BASE = "/games/balls-of-fire/audio/source";
const SRC = {
  freeMusic: `${BASE}/01_Free_Games_Background_Music.wav`,
  bbMusic: `${BASE}/02_Burning_Balls_Bonus_Background.wav`,
  bbStinger: `${BASE}/03_Burning_Balls_Trigger_Stinger.wav`,
  bbLock: `${BASE}/04_Burning_Ball_Landing_Lock_Cue.wav`,
};
// Extra (non-source) tracks. `freeVox` is the owner's Free-Games recording,
// played back a little HIGHER-PITCHED (preservesPitch off + >1 rate).
const EXTRA = {
  freeVox: `/games/balls-of-fire/audio/free_games_voice.m4a`,
  bbVox: `/games/balls-of-fire/audio/burning_voice.m4a`,
  tcMusic: `/games/thunder-cash/main-music.m4a`,
};
const TRACKS = { ...SRC, ...EXTRA };
const RATE = { freeVox: 1.12 }; // ~ +2 semitones (a little higher pitch)
const DUCK = 0.56; // ~ -5 dB
const MUSIC_MASTER = 0.7; // global music trim (all features) — turned down a little per owner

const ls = (k, d) => {
  const v = typeof localStorage !== "undefined" ? localStorage.getItem(`bof_audio_${k}`) : null;
  return v == null ? d : v;
};

class BofAudio {
  constructor() {
    this.musicVol = parseFloat(ls("music", "0.7"));
    this.sfxVol = parseFloat(ls("sfx", "0.9"));
    this.musicScale = 1; // per-game music multiplier (Thunder Cash sets 0.7; BOF stays 1)
    this.sfxScale = 1;   // per-game sfx multiplier (Thunder Cash sets >1; BOF stays 1)
    this.baseKey = "bbMusic"; // which track is the "base game" loop (Thunder Cash swaps this)
    this.baseVolScale = 1;    // extra volume trim applied ONLY to the base-game track
    this.muted = ls("muted", "0") === "1";
    this.music = null; // { el, key }
    this.ducked = false;
    this._duckTimer = null;
    this._raf = new WeakMap();
  }
  getState() { return { musicVol: this.musicVol, sfxVol: this.sfxVol, muted: this.muted }; }
  setMusicVol(v) { this.musicVol = v; localStorage.setItem("bof_audio_music", String(v)); this._applyMusic(); }
  setSfxVol(v) { this.sfxVol = v; localStorage.setItem("bof_audio_sfx", String(v)); }
  setMuted(m) { this.muted = m; localStorage.setItem("bof_audio_muted", m ? "1" : "0"); this._applyMusic(); }
  _target() { const baseMul = (this.music && this.music.key === this.baseKey) ? this.baseVolScale : 1; return this.muted ? 0 : this.musicVol * MUSIC_MASTER * this.musicScale * baseMul * (this.ducked ? DUCK : 1); }
  _applyMusic() { if (this.music) this.music.el.volume = this._target(); }

  _fade(el, to, ms, done) {
    const from = el.volume, t0 = performance.now();
    const prev = this._raf.get(el); if (prev) cancelAnimationFrame(prev);
    const step = (now) => {
      const p = Math.min(1, (now - t0) / Math.max(1, ms));
      el.volume = Math.max(0, Math.min(1, from + (to - from) * p));
      if (p < 1) this._raf.set(el, requestAnimationFrame(step)); else done && done();
    };
    this._raf.set(el, requestAnimationFrame(step));
  }

  // Loop a music track. Idempotent: if the SAME key is already playing, do
  // nothing (recovery-safe — no restart, no stinger replay).
  playMusic(key, { fadeIn = 350 } = {}) {
    if (this.music && this.music.key === key) return;
    this.stopMusic({ fadeOut: 200 });
    const el = new Audio(TRACKS[key]); el.loop = true; el.preload = "auto"; el.volume = 0;
    const rate = RATE[key];
    if (rate) {
      el.preservesPitch = false; el.mozPreservesPitch = false; el.webkitPreservesPitch = false;
      el.playbackRate = rate; // pitch it up a little (preservesPitch off → higher pitch)
    }
    this.music = { el, key };
    el.play().catch(() => {});
    this._fade(el, this._target(), fadeIn);
  }
  stopMusic({ fadeOut = 350 } = {}) {
    const m = this.music; if (!m) return; this.music = null; this.ducked = false;
    this._fade(m.el, 0, fadeOut, () => { try { m.el.pause(); } catch (e) {} });
  }
  currentMusic() { return this.music ? this.music.key : null; }

  // One-shot cue (never loops). Overlap allowed. Ducks music while it plays.
  playSfx(key) {
    const a = new Audio(SRC[key]); a.volume = this.muted ? 0 : Math.min(1, this.sfxVol * this.sfxScale); a.play().catch(() => {});
    this.duck(true);
    if (this._duckTimer) clearTimeout(this._duckTimer);
    this._duckTimer = setTimeout(() => this.duck(false), 1400);
  }

  // One-shot VOICE/recording (never loops). Plays OVER the music (music keeps
  // playing, ducked a touch). Applies the same pitch-up as the looped version.
  playVoice(key) {
    const a = new Audio(TRACKS[key]);
    const rate = RATE[key];
    if (rate) {
      a.preservesPitch = false; a.mozPreservesPitch = false; a.webkitPreservesPitch = false;
      a.playbackRate = rate;
    }
    a.volume = this.muted ? 0 : Math.min(1, this.sfxVol * this.sfxScale);
    a.play().catch(() => {});
    this.duck(true);
    if (this._voiceDuckT) clearTimeout(this._voiceDuckT);
    const un = () => this.duck(false);
    a.addEventListener("ended", un, { once: true });
    this._voiceDuckT = setTimeout(un, 9000); // safety un-duck
  }
  duck(on) { if (this.ducked === on) return; this.ducked = on; this._applyMusic(); }

  // Play the trigger stinger ONCE, then start the bonus loop after it ends.
  stingerThenMusic(stingerKey, musicKey) {
    this.stopMusic({ fadeOut: 150 });
    const a = new Audio(SRC[stingerKey]); a.volume = this.muted ? 0 : Math.min(1, this.sfxVol * this.sfxScale); a.play().catch(() => {});
    let started = false;
    const start = () => { if (started) return; started = true; this.playMusic(musicKey, { fadeIn: 300 }); };
    a.addEventListener("ended", start, { once: true });
    // fallback in case 'ended' is missed
    setTimeout(start, 6000);
  }

  // Recovery: resume bonus music WITHOUT the stinger.
  resumeMusic(key) { this.playMusic(key, { fadeIn: 200 }); }

  // Base game loops the Burning Balls background track (owner request).
  // Ramps the (possibly primed/silent) base element up to target, or starts it fresh.
  baseMusic() {
    if (this.music && this.music.key === this.baseKey) {
      if (this.music.el.paused) this.music.el.play().catch(() => {});
      this._fade(this.music.el, this._target(), 400);
      return;
    }
    this.playMusic(this.baseKey, { fadeIn: 500 });
  }

  // Silently unlock the base track during the loading screen (called inside a user
  // gesture): starts bbMusic at volume 0 so autoplay policy is satisfied; baseMusic()
  // later ramps it up the moment the game opens — no second gesture needed.
  primeBase() {
    this._ctx();
    if (this.music && this.music.key === this.baseKey) return;
    const el = new Audio(TRACKS[this.baseKey]); el.loop = true; el.preload = "auto"; el.volume = 0;
    this.music = { el, key: this.baseKey };
    el.play().catch(() => {});
  }

  // First-user-gesture unlock: resume the synth context AND (re)start the music
  // that SHOULD be playing. Recovers from a blocked autoplay attempt where the
  // element was registered but left paused by the browser's autoplay policy.
  kick() {
    this._ctx(); // resume/prime the synth AudioContext on a real gesture
    const m = this.music;
    if (!m) { this.baseMusic(); return; }
    if (m.el.paused) { m.el.play().catch(() => {}); this._applyMusic(); }
  }

  // ---- Procedural (synthesized, royalty-free) SFX bus ----------------------
  // Reel start/stop + win/big-win/coin cues are generated in-browser via Web
  // Audio (no files, no licensing). They obey the same mute + sfxVol controls.
  _ctx() {
    if (!this._ac) {
      const AC = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
      if (!AC) return null;
      this._ac = new AC();
    }
    if (this._ac.state === "suspended") this._ac.resume().catch(() => {});
    return this._ac;
  }
  _sfxGain() { return this.muted ? 0 : Math.min(1, this.sfxVol * this.sfxScale); }
  _tone(ac, freq, startOffset, dur, vol, type = "sine") {
    const o = ac.createOscillator(), g = ac.createGain();
    o.type = type; o.frequency.value = freq;
    const t = ac.currentTime + startOffset;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0001, vol), t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(ac.destination);
    o.start(t); o.stop(t + dur + 0.02);
    o.onended = () => { try { o.disconnect(); g.disconnect(); } catch (e) {} }; // release nodes (mobile leak fix)
  }
  _noise(ac, startOffset, dur, vol, freq = 1200, q = 0.7) {
    const n = Math.floor(ac.sampleRate * dur);
    const buf = ac.createBuffer(1, n, ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
    const src = ac.createBufferSource(); src.buffer = buf;
    const bp = ac.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = freq; bp.Q.value = q;
    const g = ac.createGain();
    const t = ac.currentTime + startOffset;
    g.gain.setValueAtTime(Math.max(0.0001, vol), t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(bp); bp.connect(g); g.connect(ac.destination);
    src.start(t); src.stop(t + dur + 0.02);
    src.onended = () => { try { src.disconnect(); bp.disconnect(); g.disconnect(); } catch (e) {} }; // release nodes
  }
  sfxReelStart() {
    const ac = this._ctx(); if (!ac) return; const v = this._sfxGain(); if (!v) return;
    const o = ac.createOscillator(), g = ac.createGain(), lp = ac.createBiquadFilter();
    o.type = "sawtooth"; lp.type = "lowpass"; lp.frequency.value = 900;
    const t = ac.currentTime;
    o.frequency.setValueAtTime(140, t); o.frequency.exponentialRampToValueAtTime(520, t + 0.22);
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.10 * v, t + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
    o.connect(lp); lp.connect(g); g.connect(ac.destination); o.start(t); o.stop(t + 0.3);
    o.onended = () => { try { o.disconnect(); lp.disconnect(); g.disconnect(); } catch (e) {} };
    this._noise(ac, 0, 0.26, 0.05 * v, 1600, 0.5);
  }
  sfxReelStop() {
    const ac = this._ctx(); if (!ac) return; const v = this._sfxGain(); if (!v) return;
    this._tone(ac, 230 + Math.random() * 60, 0, 0.09, 0.22 * v, "triangle");
    this._noise(ac, 0, 0.05, 0.10 * v, 2600, 1.2);
  }
  sfxWin() {
    const ac = this._ctx(); if (!ac) return; const v = this._sfxGain(); if (!v) return;
    [523.25, 659.25, 783.99].forEach((f, i) => this._tone(ac, f, i * 0.09, 0.22, 0.16 * v, "sine"));
  }
  sfxBigWin() {
    const ac = this._ctx(); if (!ac) return; const v = this._sfxGain(); if (!v) return;
    [523.25, 659.25, 783.99, 1046.5, 1318.5, 1567.98].forEach((f, i) => {
      this._tone(ac, f, i * 0.10, 0.30, 0.17 * v, "triangle");
      this._tone(ac, f * 2, i * 0.10, 0.18, 0.06 * v, "sine");
    });
  }
  sfxCoin() {
    const ac = this._ctx(); if (!ac) return; const v = this._sfxGain(); if (!v) return;
    this._tone(ac, 1180, 0, 0.10, 0.12 * v, "square");
    this._tone(ac, 1760, 0.03, 0.10, 0.09 * v, "square");
  }
  // Punchy impact when a fireball (BALL) lands — audible on phone/laptop speakers
  // (mid-range body + bright attack transient, not just sub-bass).
  sfxBoom() {
    const ac = this._ctx(); if (!ac) return; const v = this._sfxGain(); if (!v) return;
    const t = ac.currentTime;
    // BODY: triangle drop 440->110 → harmonics small speakers can reproduce
    const o = ac.createOscillator(), g = ac.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(440, t); o.frequency.exponentialRampToValueAtTime(110, t + 0.22);
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.55 * v, t + 0.010);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.38);
    o.connect(g); g.connect(ac.destination); o.start(t); o.stop(t + 0.4);
    o.onended = () => { try { o.disconnect(); g.disconnect(); } catch (e) {} };
    // SUB: adds weight on capable speakers
    const o2 = ac.createOscillator(), g2 = ac.createGain();
    o2.type = "sine"; o2.frequency.setValueAtTime(150, t); o2.frequency.exponentialRampToValueAtTime(58, t + 0.3);
    g2.gain.setValueAtTime(0.0001, t); g2.gain.exponentialRampToValueAtTime(0.4 * v, t + 0.012);
    g2.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    o2.connect(g2); g2.connect(ac.destination); o2.start(t); o2.stop(t + 0.42);
    o2.onended = () => { try { o2.disconnect(); g2.disconnect(); } catch (e) {} };
    // ATTACK: bright noise transient so the hit is crisp everywhere
    this._noise(ac, 0, 0.06, 0.26 * v, 3200, 0.8);
  }
  // Clear bright sparkle when a SCATTER lands — a bell ping (now audible).
  sfxScatter() {
    const ac = this._ctx(); if (!ac) return; const v = this._sfxGain(); if (!v) return;
    this._tone(ac, 1320, 0, 0.34, 0.30 * v, "sine");
    this._tone(ac, 1980, 0.05, 0.30, 0.18 * v, "sine");
    this._noise(ac, 0, 0.04, 0.12 * v, 5000, 1.0); // shimmer transient
  }

  stopAll() { this.stopMusic({ fadeOut: 200 }); }
}

export const bofAudio = new BofAudio();
export const AUDIO_KEYS = TRACKS;
